// Central content source for the Amden Operations page.
// Structured so a future website admin area can manage every field
// without touching page components.

export const OPERATIONS_SEO = {
  title: 'Operations Consultancy & Business Systems | Amden Group',
  description:
    'Improve processes, accountability, reporting and operational performance with practical consultancy and scalable business systems from Amden Group.',
};

export const OPERATIONS_HERO = {
  eyebrow: 'Amden Operations',
  headingLines: ['Improve efficiency.', 'Gain control.', 'Scale with confidence.'],
  accentLineIndex: 2,
  body: 'Amden helps businesses strengthen the way they operate. We combine practical consultancy, process improvement and intelligent systems to remove bottlenecks, improve accountability and give management clearer control.',
  primaryCta: {
    label: 'Discuss Your Operations',
    href: '/?service=operations#solution-finder',
  },
  image: {
    src: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80',
    alt: 'Operations consultant reviewing plans and performance with a business team around a table',
    width: 1200,
    height: 900,
  },
  floatingCard: {
    label: 'Consultancy first',
    text: 'Systems support the work — practical operational improvement leads it.',
  },
};

export const OPERATIONS_WHAT_WHO = {
  whatHeading: 'What We Do',
  whatCopy:
    'We analyse how your business currently operates, identify what is slowing it down and implement practical improvements. Our work can include operational strategy, process design, accountability structures, performance reporting, system selection, automation and hands-on implementation.',
  whatAreas: [
    'Operational reviews and business diagnostics',
    'Process mapping and workflow improvement',
    'Roles, responsibilities and accountability',
    'Performance management, reporting and KPIs',
    'Management structures and meeting rhythms',
    'Systems assessment, selection and integration',
    'Automation and reduction of manual administration',
    'Implementation support and change management',
    'Bespoke operational systems and CRM solutions',
    'Ongoing operational improvement',
  ],
  whoHeading: 'Who It’s For',
  whoCopy:
    'Amden Operations supports businesses that recognise the need for improvement but require experienced, practical support to make that improvement happen.',
  whoAreas: [
    'Growing businesses experiencing operational pressure',
    'Owner-managed companies relying too heavily on the owner',
    'Teams with unclear responsibilities or weak accountability',
    'Businesses using inefficient manual processes',
    'Organisations with disconnected information or systems',
    'Management teams lacking reliable reporting and visibility',
    'Multi-site businesses struggling with consistency',
    'Companies preparing to scale, restructure or implement change',
  ],
};

export const OPERATIONS_PROBLEMS = {
  eyebrow: 'Problems We Solve',
  heading: 'Problems We Solve',
  intro:
    'Most operational problems are connected. Weak processes create poor information, unclear accountability and unnecessary pressure. We help identify the cause and put practical solutions in place.',
  items: [
    {
      id: 'inefficient-processes',
      icon: 'workflow',
      title: 'Inefficient Processes',
      description: 'Work is slowed down by unnecessary steps, duplication, delays or unclear workflows.',
      howWeHelp:
        'We map how work actually flows through your business, remove duplication and delays, and redesign processes around clear, efficient ways of working.',
    },
    {
      id: 'lack-of-visibility',
      icon: 'eye',
      title: 'Lack of Operational Visibility',
      description:
        'Management cannot easily see performance, workload, issues or priorities across the business.',
      howWeHelp:
        'We introduce practical reporting structures and management rhythms so performance, workload and priorities are always visible to the people who need them.',
    },
    {
      id: 'weak-accountability',
      icon: 'clipboard-check',
      title: 'Weak Accountability',
      description: 'Actions are agreed but ownership, deadlines and follow-through are unclear.',
      howWeHelp:
        'We clarify roles, ownership and deadlines — supported by meeting structures and simple tracking that makes follow-through the norm.',
    },
    {
      id: 'disconnected-systems',
      icon: 'unlink',
      title: 'Disconnected Systems and Information',
      description:
        'Teams rely on separate spreadsheets, emails, paper records or systems that do not work together.',
      howWeHelp:
        'We consolidate information and connect — or replace — the systems behind it, so your team works from one reliable picture of the business.',
    },
    {
      id: 'manual-administration',
      icon: 'files',
      title: 'Excessive Manual Administration',
      description: 'Skilled employees lose time repeatedly entering, chasing or reorganising information.',
      howWeHelp:
        'We reduce repetitive administration through better process design, sensible automation and systems that are fit for purpose.',
    },
    {
      id: 'inconsistent-working',
      icon: 'shuffle',
      title: 'Inconsistent Ways of Working',
      description:
        'Different people, teams or locations follow different processes, creating errors and variable performance.',
      howWeHelp:
        'We standardise processes across people, teams and locations — documented clearly and kept practical enough to be followed every day.',
    },
    {
      id: 'scaling-challenges',
      icon: 'trending-up',
      title: 'Scaling Challenges',
      description:
        'Existing processes and structures can no longer support the size or complexity of the business.',
      howWeHelp:
        'We build operating structures that scale with you — so growth stops meaning more chaos and starts meaning more control.',
    },
    {
      id: 'poor-reporting',
      icon: 'bar-chart',
      title: 'Poor Reporting and KPIs',
      description: 'Reports are late, unreliable or focused on activity rather than meaningful performance.',
      howWeHelp:
        'We define meaningful KPIs and reporting rhythms that focus management attention on performance, not just activity.',
    },
    {
      id: 'owner-overdependence',
      icon: 'user-cog',
      title: 'Overdependence on the Owner',
      description:
        'Decisions and knowledge remain concentrated with one person, limiting delegation and growth.',
      howWeHelp:
        'We distribute knowledge, delegate decision-making and build management structures that reduce reliance on any one person.',
    },
    {
      id: 'systems-dont-fit',
      icon: 'puzzle',
      title: 'Systems That Do Not Fit the Business',
      description:
        'Existing software is underused, poorly configured or forces the business into unsuitable workflows.',
      howWeHelp:
        'We assess, configure or build systems around the way your business actually works — never the other way around.',
    },
  ],
};

export const OPERATIONS_DELIVERY = {
  eyebrow: 'How We Deliver',
  heading: 'A structured route to better operations',
  stages: [
    {
      number: '01',
      title: 'Review & Analysis',
      text: 'We assess your current operations, speak with the relevant people and identify inefficiencies, risks and opportunities.',
    },
    {
      number: '02',
      title: 'Strategic Recommendations',
      text: 'We provide a structured improvement plan with clear priorities, responsibilities and expected outcomes.',
    },
    {
      number: '03',
      title: 'Implementation & Support',
      text: 'We work alongside your team to introduce the agreed changes, build the required systems and support adoption.',
    },
    {
      number: '04',
      title: 'Ongoing Optimisation',
      text: 'We monitor performance, resolve emerging issues and continuously improve how the business operates.',
    },
  ],
  diagramLabel:
    'Continuous improvement cycle: review and analysis, strategic recommendations, implementation and support, then ongoing optimisation.',
};

/**
 * Amden systems — data-driven and future-purchase-ready.
 *
 * DEVELOPER NOTE (future e-commerce):
 * - `slug` and `href` are the stable identifiers for future system-detail pages.
 * - `purchasable` stays false until direct Stripe purchase is approved.
 * - `price` is reserved for a future approved pricing model — do not display
 *   pricing until it has been supplied and approved by Amden Group.
 */
export const AMDEN_SYSTEMS = [
  {
    slug: 'compass',
    name: 'Amden Compass',
    purpose:
      'Business planning, coaching, actions and performance visibility in one structured management platform.',
    icon: 'compass',
    href: '/systems/compass',
    purchasable: false,
    price: null,
  },
  {
    slug: 'pulse',
    name: 'Amden Pulse',
    purpose:
      'Workforce timesheets, approvals, leave, compliance, labour costs and site and customer profitability in one connected platform.',
    icon: 'activity',
    href: '/systems/pulse',
    purchasable: false,
    price: null,
  },
  {
    slug: 'foresee',
    name: 'Amden Foresee',
    purpose:
      'Frontline reporting, actions, compliance visibility, contractor management and multi-site EHSQ oversight in one connected platform.',
    icon: 'shield-check',
    href: '/systems/foresee',
    purchasable: false,
    price: null,
  },
  {
    slug: 'reach',
    name: 'Amden Reach',
    purpose:
      'Sales contacts, pipeline, targets and field-sales activity in a clear, practical CRM.',
    icon: 'target',
    href: '/amden-reach',
    purchasable: false,
    price: null,
  },
];

export const SYSTEMS_SECTION = {
  eyebrow: 'Amden Systems',
  heading: 'Systems that support better operations',
  intro:
    'Where technology can improve visibility, consistency and control, Amden can implement an existing solution or develop a workflow around the way your business actually operates.',
};

export const OPERATIONS_CASE_STUDY = {
  eyebrow: 'Amden Operations',
  heading: 'KQM Electrical Operational App',
  intro:
    'A bespoke operational system built around how the business actually works — because the workflow came first, not the software.',
  challenge: {
    title: 'Challenge',
    text: 'KQM Electrical needed a better way to manage day-to-day operational workflows. Manual administration was slowing the team down, and management lacked a clear, reliable view of work across the business.',
  },
  solution: {
    title: 'Solution',
    text: 'Amden worked alongside the KQM team to understand their operational workflow first — then designed and implemented a bespoke operational app around it, supported by practical process improvements and hands-on implementation support.',
  },
  results: {
    title: 'Results',
    points: [
      'Reduced manual administration',
      'Improved operational visibility',
      'Faster reporting and workflow management',
    ],
  },
  cta: { label: 'Read Full Case Study', href: '/case-studies/kqm-electrical' },
};

export const OPERATIONS_CTA = {
  index: '06',
  eyebrow: 'Next step',
  heading: 'Ready to improve how your business operates?',
  copy: 'Let’s identify what is creating pressure, limiting visibility or slowing growth—and put a practical improvement plan in place.',
  primaryLabel: 'Discuss Your Operations',
  primaryHref: '/?service=operations#solution-finder',
  secondaryLabel: 'Explore the Ecosystem',
  secondaryHref: '/ecosystem',
};
